import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { useMemo } from 'react';

import { FEATURE_FLAGS } from '~/modules/experiments/features';
import { useFeatureFlags } from '~/modules/experiments/hooks/useFeatureFlags';

import type { CurrentAmplifyUser } from '../api/ampUser';
import { getCurrentAmplifyUser } from '../api/ampUser';
import { AmplifyUserContext } from './AmplifyUserContext';

const USER_QUERY_KEY = ['amplifyUser'];

export function AmplifyUserProvider({ children }: { children: ReactNode }) {
  const [isInMaintenanceModeEnabled] = useFeatureFlags([
    FEATURE_FLAGS.WmgLabelPerfMaintenanceEnabled,
  ]);

  const isInMaintenanceMode = useMemo(() => {
    const localStorageMaintenance =
      localStorage.getItem(FEATURE_FLAGS.WmgLabelPerfMaintenanceEnabled) ===
      'true';

    return localStorageMaintenance || isInMaintenanceModeEnabled;
  }, [isInMaintenanceModeEnabled]);

  const queryClient = useQueryClient();

  const {
    data: user,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery<CurrentAmplifyUser>({
    queryKey: USER_QUERY_KEY,
    queryFn: getCurrentAmplifyUser,
    staleTime: Infinity,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    enabled: !isInMaintenanceMode,
  });

  const refetchUser = () => {
    refetch();
  };

  const clearUserCache = () => {
    queryClient.removeQueries({ queryKey: USER_QUERY_KEY });
  };

  return (
    <AmplifyUserContext.Provider
      value={{ user, isLoading, isError, error, refetchUser, clearUserCache }}
    >
      {children}
    </AmplifyUserContext.Provider>
  );
}

AmplifyUserProvider.displayName = 'AmplifyUserProvider';
